// Optional "Totale e gruppi" mode of the historic tickets search.
// Kept free of React, DevExtreme and path aliases so Node can load it in the checks.
//
// The grid has three modes:
// - 'browse'  : no committed search; unchanged legacy browsing (count, grouping, virtual scrolling).
// - 'search'  : committed search, toggle OFF (default). Fast path: infinite scrolling, 20 rows per
//               page, never a count, never grouping.
// - 'counted' : committed search, toggle ON (explicit opt-in). Same native search request plus
//               $count=true on the paged root request, so the exact number of tickets is shown.
//               Grouping is offered but applied only when the user actually groups; the grid
//               starts ungrouped on its first page.
//
// The opt-in belongs to ONE committed text and ONE user filter. The user's logical filter (filter
// panel value plus each column's filter-row/header-filter fields) is captured AT THE TOGGLE as the
// consent. Every request of the counted grid is admitted only while the grid's CURRENT logical
// filter still equals that consent (same canonical form), so a field/header/filter-panel change,
// even one made before the first counted request, is refused before sending and turns the toggle
// OFF. The grid's own group requests add group conditions to $filter but not to the logical user
// filter, so grouping, collapsing and expanding never reset the opt-in; nor do sorting, layout,
// paging or token refreshes. A new committed text turns it OFF.
//
// Every replacement of the grid (new text, browse/search, ON/OFF, stale session) advances a
// page-wide monotonic generation synchronously, before any React state change: requests, layout
// saves and stale callbacks of earlier generations are refused/ignored from that moment on.

export type HistoricGridMode = 'browse' | 'search' | 'counted'

export const COUNT_TOGGLE_LABEL = 'Abilita raggruppamento su questa ricerca'

/**
 * Search box width. Desktop unchanged (400px). Extra-small screens (< 600px) keep the existing 250px
 * when the toggle is hidden; with the toggle shown, the box narrows to 190px and the toggle shows the
 * switch only (its full label stays as accessible name and tooltip), so both fit in 320px.
 */
export const historicSearchBoxWidth = (isXSmall: boolean, toggleVisible: boolean): string => {
  if (!isXSmall) return '400px'
  return toggleVisible ? '190px' : '250px'
}

export const historicGridMode = (committedSearch: string | null, countRequested: boolean): HistoricGridMode => {
  if (committedSearch === null) return 'browse'
  return countRequested ? 'counted' : 'search'
}

/** Layout-store mode: counted grids save like search grids, so their grouping/paging never
 * overwrite the browse layout (column edits are folded into browse, as for search). */
export const layoutModeOf = (mode: HistoricGridMode): 'browse' | 'search' => (mode === 'browse' ? 'browse' : 'search')

/** Grid key: unique per generation (monotonic), so no earlier grid key is ever reused. */
export const historicGridKey = (mode: HistoricGridMode, generation: number): string => `${mode}:${generation}`

/** Grid options per mode. The page memoizes them per mode, so re-renders do not reset the grid. */
export const historicGridModeOptions = (mode: HistoricGridMode) => {
  const groupingAllowed = mode !== 'search'
  return {
    remoteOperations: {
      filtering: true,
      grouping: false,
      groupPaging: false,
      paging: true,
      sorting: true,
      summary: true,
    },
    grouping: { autoExpandAll: true, contextMenuEnabled: groupingAllowed, allowCollapsing: true },
    groupPanel: {
      visible: groupingAllowed,
      allowColumnDragging: groupingAllowed,
      emptyPanelText: 'Trascina qui una colonna per raggruppare',
    },
    /** Fast search: no grouping at all (it would reintroduce counts and larger loads). */
    disableGrouping: !groupingAllowed,
    /** Browse and counted need a total (virtual scrolling); fast search never asks for one. */
    scrollingMode: mode === 'search' ? ('infinite' as const) : ('virtual' as const),
    /** The pager shows the exact number of tickets ({2} = @odata.count), as in browse mode. */
    pagerVisible: mode !== 'search',
    searchPaging: mode !== 'browse',
  }
}

export const STALE_COUNTED_SEARCH = 'historic-counted-search-stale'

export class StaleCountedSearchError extends Error {
  readonly code = STALE_COUNTED_SEARCH
  constructor() {
    super('I filtri sono cambiati: totale e gruppi disattivati.')
    this.name = 'StaleCountedSearchError'
  }
}

export const RETIRED_SEARCH_GRID = 'historic-search-grid-retired'

/** A request from a grid/store that the page already replaced (old text, mode or generation). */
export class RetiredSearchGridError extends Error {
  readonly code = RETIRED_SEARCH_GRID
  constructor() {
    super('Richiesta di una ricerca precedente: non inviata.')
    this.name = 'RetiredSearchGridError'
  }
}

/** True for the guards' own refusals (also when DevExtreme wraps them): the page does not notify
 * them, because the page itself already switched mode or replaced the grid. */
export const isHistoricGuardRefusal = (error: unknown): boolean => {
  const codes = [STALE_COUNTED_SEARCH, RETIRED_SEARCH_GRID]
  const messages = [new StaleCountedSearchError().message, new RetiredSearchGridError().message]
  let e: unknown = error
  for (let depth = 0; e !== null && typeof e === 'object' && depth < 4; depth++) {
    if (codes.includes((e as { code?: string }).code ?? '')) return true
    if (e instanceof Error && messages.includes(e.message)) return true
    const next = e as { errorDetails?: unknown; cause?: unknown }
    e = next.errorDetails ?? next.cause
  }
  return false
}

/**
 * Page-wide grid generation. Every transition (new committed text, toggle ON/OFF, stale counted
 * session) advances it SYNCHRONOUSLY, before any capture or React state update, so stores, guards
 * and layout saves of earlier generations are retired at once, even while React has not yet
 * re-rendered and DevExtreme still holds the old store.
 */
export const createGenerationGate = () => {
  let current = 0
  return {
    current: () => current,
    advance: (): number => ++current,
    isCurrent: (generation: number) => generation === current,
  }
}

export type GenerationGate = ReturnType<typeof createGenerationGate>

const FILTER_COLUMN_FIELDS = ['filterValue', 'filterValues', 'filterType', 'selectedFilterOperation'] as const
const isMissing = (value: unknown) =>
  value === undefined || value === null || (Array.isArray(value) && value.length === 0)

/**
 * Canonical signature of the filter part of a stored grid layout: the filter panel value and its
 * enabled flag (the panel checkbox; missing = enabled, the DataGrid default) plus each column's
 * filter fields, by data field or name, in a stable order. Layout, sorting, grouping and paging are
 * ignored; missing, null and empty values are equivalent.
 */
export const layoutFilterSignature = (state: unknown): string => {
  const layout = (typeof state === 'object' && state !== null ? state : {}) as {
    filterValue?: unknown
    filterPanel?: { filterEnabled?: unknown }
    columns?: unknown
  }
  const filterEnabled = layout.filterPanel?.filterEnabled !== false
  const columns: [string, Record<string, unknown>][] = []
  for (const column of Array.isArray(layout.columns) ? layout.columns : []) {
    if (typeof column !== 'object' || column === null) continue
    const c = column as Record<string, unknown>
    let key = ''
    if (typeof c.dataField === 'string') key = c.dataField
    else if (typeof c.name === 'string') key = `name:${c.name}`
    const fields: Record<string, unknown> = {}
    for (const field of FILTER_COLUMN_FIELDS) if (!isMissing(c[field])) fields[field] = c[field]
    if (Object.keys(fields).length > 0) columns.push([key, fields])
  }
  columns.sort(([a], [b]) => a.localeCompare(b))
  return JSON.stringify({
    filterValue: isMissing(layout.filterValue) ? null : layout.filterValue,
    filterEnabled,
    columns,
  })
}

/** The grid instance API used to read the CURRENT effective user filter (public DataGrid methods). */
export type FilterSource = {
  option(name: 'filterPanel.filterEnabled'): unknown
  getCombinedFilter(returnDataField: boolean): unknown
}

/** The live grid API used when capturing its layout before a replacement (public DataGrid methods). */
export type LiveGrid = FilterSource & {
  state(): unknown
  option(name: 'filterValue'): unknown
  pageSize(): number
  pageIndex(): number
}

/**
 * The grid's state() comes from its state-storing cache, which can lag behind a filter option
 * changed in the same task (the filter panel checkbox or filter builder value, read by the request
 * guard before the delayed save) or a page size/index. The live public options win, so the
 * replacement grid restores the effective filter and paging the user actually set.
 */
export const stateWithLiveFilterOptions = (grid: LiveGrid | undefined): unknown => {
  const state = grid?.state()
  if (!grid || typeof state !== 'object' || state === null) return state
  const cached = state as { filterPanel?: Record<string, unknown> }
  return {
    ...state,
    filterValue: grid.option('filterValue') ?? null,
    filterPanel: { ...cached.filterPanel, filterEnabled: grid.option('filterPanel.filterEnabled') !== false },
    // Paging too: a page size/index set just before leaving browse may not be in the cache yet.
    // (Search/counted captures fold into browse WITHOUT their paging, so browse paging is kept.)
    pageSize: grid.pageSize(),
    pageIndex: grid.pageIndex(),
  }
}

/**
 * Canonical EFFECTIVE filter of a live grid: the public getCombinedFilter(true) (filter row, header
 * filters and filter panel, as the grid applies them, with the fixed historic scope; never the
 * group filters added while loading groups) plus the panel's enabled flag (missing = enabled).
 * It does not depend on how the grid stores redundant filter fields (column filterValue/filterType
 * vs the synchronized filterValue), which can be represented differently after a state round trip.
 * Dates compare by instant; functions are ignored. `undefined` grid yields a distinct signature.
 */
export const liveFilterSignature = (grid: FilterSource | undefined): string => {
  if (!grid) return '[no-grid]'
  try {
    return JSON.stringify(
      {
        filter: grid.getCombinedFilter(true) ?? null,
        filterEnabled: grid.option('filterPanel.filterEnabled') !== false,
      },
      (_key, value: unknown) => (typeof value === 'function' ? '[fn]' : value),
    )
  } catch {
    return '[unserializable]'
  }
}

/**
 * Guards ONE counted grid instance (one generation).
 * - Consent: `consent` is the logical user filter signature captured AT THE TOGGLE. Every request,
 *   including the first, is admitted only while `currentSignature()` (same canonical form, read
 *   from the live grid at send time) equals it; otherwise it is refused before sending and
 *   `onStale` runs once (the page turns the toggle OFF).
 * - Retirement: once its generation is no longer current, every request is refused and `onStale`
 *   is never called, so an old session can neither send, capture the current grid nor turn OFF a
 *   newer ON session.
 */
export const createCountedSessionGuard = (options: {
  consent: string
  currentSignature: () => string
  isCurrent: () => boolean
  onStale: () => void
}) => {
  let stale = false
  return {
    beforeRequest(): void {
      if (!options.isCurrent()) throw new RetiredSearchGridError()
      if (stale) throw new StaleCountedSearchError()
      if (options.currentSignature() === options.consent) return
      stale = true
      options.onStale()
      throw new StaleCountedSearchError()
    },
    isStale: () => stale,
  }
}

export type CountedSessionGuard = ReturnType<typeof createCountedSessionGuard>
